import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { dimensionLabel, dimensionShort } from "../lib/scoring";
import MarkButton from "./MarkButton";

/**
 * Sprint 7 — Platform benchmark for this deployment.
 *
 * One backend call: GET /api/benchmark/:deploymentId. Returns per-dimension
 * corpus placement (top quartile / mid / bottom quartile) against the
 * P25 / median / P75 of every other deployment that has crossed the n>=5
 * threshold. If the corpus has fewer than 5 contributing deployments, the
 * endpoint returns {insufficient: true} and we show a quiet panel saying
 * the corpus is building.
 *
 * Tab placement: between Compare and Cohorts in DeploymentDetail.
 */
const DIM_ORDER = [
  "optimism", "emotional_regulation", "ownership", "flexibility",
  "social_support", "purpose", "grit",
];

export default function Benchmark({ deploymentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data: payload } = await api.get(`/api/benchmark/${deploymentId}`);
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [deploymentId]);

  if (loading) {
    return <div className="text-sm text-gray-500" data-testid="benchmark-loading">Loading…</div>;
  }
  if (error) {
    return <div className="text-sm" style={{ color: "#AD393E" }} data-testid="benchmark-error">{error}</div>;
  }
  if (!data) return null;

  return (
    <div data-testid="benchmark">
      <BenchmarkHeader data={data} />
      {data.insufficient ? (
        <InsufficientPanel data={data} />
      ) : !data.team_in_corpus ? (
        <NotInCorpusPanel data={data} />
      ) : (
        <PlacementTable placements={data.placements} />
      )}
    </div>
  );
}

function BenchmarkHeader({ data }) {
  const corpusCount = data?.corpus_n_deployments ?? 0;
  return (
    <div className="mb-6" data-testid="benchmark-header">
      <div className="eyebrow mb-1">Benchmark</div>
      <h3 className="text-lg mb-3" style={{ fontFamily: "Inter", fontWeight: 500 }}>
        Where this team sits in the corpus.
      </h3>
      <p className="text-sm text-gray-700 max-w-prose">
        For each dimension, the team&rsquo;s mean is placed against the platform
        corpus — every deployment that has crossed five completed
        respondents. Corpus today: {corpusCount} deployment{corpusCount === 1 ? "" : "s"}.
        The corpus is anonymized at the aggregate level; no respondent or
        client data is exposed by this view.
      </p>
    </div>
  );
}

function InsufficientPanel({ data }) {
  return (
    <div className="panel" data-testid="benchmark-insufficient">
      <div className="eyebrow mb-2" style={{ color: "#AD393E" }}>Benchmark is building</div>
      <p className="text-sm text-gray-700 max-w-prose">
        We need at least {data.min_required} comparable deployments to surface a
        meaningful corpus distribution. Right now the platform has
        {" "}{data.corpus_n_deployments} contributing deployment
        {data.corpus_n_deployments === 1 ? "" : "s"}. We&rsquo;ll release this view
        once the corpus crosses the threshold.
      </p>
    </div>
  );
}

function NotInCorpusPanel({ data }) {
  return (
    <div className="panel" data-testid="benchmark-not-in-corpus">
      <div className="eyebrow mb-2">Not yet in the corpus</div>
      <p className="text-sm text-gray-700 max-w-prose">{data.message}</p>
    </div>
  );
}

function PlacementTable({ placements }) {
  const ordered = DIM_ORDER
    .map((k) => placements.find((p) => p.dimension === k))
    .filter(Boolean);
  return (
    <div className="border border-gray-100" data-testid="benchmark-table">
      <div className="grid grid-cols-[1.5fr_0.9fr_1.4fr_0.7fr] bg-cream border-b border-gray-100">
        <div className="px-4 py-3"><div className="eyebrow text-gray-500">Dimension</div></div>
        <div className="px-4 py-3"><div className="eyebrow text-gray-500">Team mean</div></div>
        <div className="px-4 py-3"><div className="eyebrow text-gray-500">Corpus (P25 · median · P75)</div></div>
        <div className="px-4 py-3 text-right"><div className="eyebrow text-gray-500">Placement</div></div>
      </div>
      {ordered.map((p) => (
        <PlacementRow key={p.dimension} placement={p} />
      ))}
    </div>
  );
}

function PlacementRow({ placement }) {
  const cls = placement.classification;
  const bg = cls === "top_quartile" ? "#D8E9EF"
    : cls === "bottom_quartile" ? "#FDE2E4"
    : cls === "mid_range" ? "#FAF7F1"
    : "#EEEEEE";
  const fg = cls === "top_quartile" ? "#2F6B7C"
    : cls === "bottom_quartile" ? "#AD393E"
    : cls === "mid_range" ? "#555555"
    : "#999";
  const label = cls === "top_quartile" ? "Top quartile"
    : cls === "bottom_quartile" ? "Bottom quartile"
    : cls === "mid_range" ? "Mid range"
    : "Insufficient";

  return (
    <div
      className="grid grid-cols-[1.5fr_0.9fr_1.4fr_0.7fr] border-t border-gray-100"
      data-testid={`benchmark-row-${placement.dimension}`}
    >
      <div className="px-4 py-4">
        <div className="text-sm" style={{ fontFamily: "Inter", fontWeight: 500 }}>
          {dimensionLabel(placement.dimension)}
        </div>
        <div className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
          {dimensionShort(placement.dimension)}
        </div>
        <div className="mt-3">
          <MarkButton
            kind="benchmark_placement"
            payload={{ dimension: placement.dimension }}
            testIdSuffix={`benchmark-${placement.dimension}`}
          />
        </div>
      </div>
      <div className="px-4 py-4" data-testid={`benchmark-team-mean-${placement.dimension}`}>
        <span style={{ fontFamily: "Oswald", fontWeight: 700, fontSize: "1.25rem" }}>
          {placement.team_mean != null ? placement.team_mean.toFixed(2) : "—"}
        </span>
      </div>
      <div className="px-4 py-4 text-sm text-gray-700" data-testid={`benchmark-corpus-${placement.dimension}`}>
        <span style={{ fontFamily: "Oswald", letterSpacing: "0.05em" }}>
          {fmtNum(placement.corpus_p25)} <span className="text-gray-400">·</span>{" "}
          <strong style={{ color: "#1A1A1A" }}>{fmtNum(placement.corpus_median)}</strong>{" "}
          <span className="text-gray-400">·</span> {fmtNum(placement.corpus_p75)}
        </span>
      </div>
      <div className="px-4 py-4 text-right" data-testid={`benchmark-placement-${placement.dimension}`}>
        <span
          className="inline-block text-xs px-3 py-1"
          style={{
            background: bg, color: fg, fontFamily: "Oswald", letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          {label}
        </span>
      </div>
    </div>
  );
}

function fmtNum(v) {
  return v == null ? "—" : v.toFixed(2);
}
